/**
 * Manager PA Alerts Center
 * Notifications / alerts center for managers.
 * Data source: GET /manager/alerts -> { summary: { total, critical, warning, info }, alerts: Alert[] }
 * Read-only (the handler exposes no mark-read / dismiss action).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

type Severity = 'critical' | 'warning' | 'info';

interface Alert {
  id: string;
  type: string;
  severity: Severity;
  title: string;
  description: string;
  clientId?: string;
  clientName?: string;
  value?: number;
  createdAt: string;
}

interface AlertsData {
  summary: { total: number; critical: number; warning: number; info: number };
  alerts: Alert[];
}

const SEVERITY_META: Record<
  Severity,
  { label: string; icon: typeof AlertTriangle; text: string; soft: string; border: string }
> = {
  critical: {
    label: 'Critical',
    icon: AlertTriangle,
    text: 'text-red-600 dark:text-red-400',
    soft: 'bg-red-500/10',
    border: 'border-red-500/20',
  },
  warning: {
    label: 'Warning',
    icon: AlertCircle,
    text: 'text-amber-700 dark:text-amber-300',
    soft: 'bg-amber-500/10',
    border: 'border-amber-500/20',
  },
  info: {
    label: 'Info',
    icon: Info,
    text: 'text-blue-600 dark:text-blue-400',
    soft: 'bg-blue-500/10',
    border: 'border-blue-500/20',
  },
};

const SEVERITY_ORDER: Severity[] = ['critical', 'warning', 'info'];

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export default function AlertsPage() {
  const [filter, setFilter] = useState<'all' | Severity>('all');

  const { data, isLoading, error, refetch, isRefetching } = useQuery<AlertsData>({
    queryKey: ['manager-alerts-page'],
    queryFn: async () => {
      const r = await apiClient.get('/manager/alerts');
      return r.data?.data || r.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto space-y-4">
        <div>
          <h2 className="text-xl font-medium text-foreground flex items-center gap-2">
            <Bell className="h-5 w-5" /> Alerts
          </h2>
        </div>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-sm text-red-600 dark:text-red-400">
            Couldn't load alerts. {(error as any)?.message || 'Please try again.'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-[var(--v4-accent)] underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const summary = data?.summary || { total: 0, critical: 0, warning: 0, info: 0 };
  const alerts = data?.alerts || [];

  const visible = filter === 'all' ? alerts : alerts.filter((a) => a.severity === filter);

  const grouped = SEVERITY_ORDER.map((sev) => ({
    sev,
    items: visible.filter((a) => a.severity === sev),
  })).filter((g) => g.items.length > 0);

  const summaryCards: { sev: Severity; count: number }[] = [
    { sev: 'critical', count: summary.critical },
    { sev: 'warning', count: summary.warning },
    { sev: 'info', count: summary.info },
  ];

  const chips: { key: 'all' | Severity; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: summary.total },
    { key: 'critical', label: 'Critical', count: summary.critical },
    { key: 'warning', label: 'Warning', count: summary.warning },
    { key: 'info', label: 'Info', count: summary.info },
  ];

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-medium text-foreground flex items-center gap-2">
            <Bell className="h-5 w-5" /> Alerts
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {summary.total === 0
              ? 'Nothing needs your attention'
              : `${summary.total} ${summary.total === 1 ? 'alert' : 'alerts'} need attention`}
          </p>
        </div>
        {isRefetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mt-1" />}
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-3 gap-3">
        {summaryCards.map(({ sev, count }) => {
          const meta = SEVERITY_META[sev];
          const Icon = meta.icon;
          return (
            <div
              key={sev}
              className={cn('bg-card border border-border rounded-xl p-4 flex flex-col gap-1')}
            >
              <Icon className={cn('h-5 w-5', meta.text)} />
              <p className="text-2xl font-bold text-foreground">{count}</p>
              <p className="text-xs text-muted-foreground">{meta.label}</p>
            </div>
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => {
          const active = filter === chip.key;
          return (
            <button
              key={chip.key}
              onClick={() => setFilter(chip.key)}
              className={cn(
                'text-xs font-medium px-3 py-1.5 rounded-full border transition',
                active
                  ? 'bg-[var(--v4-accent)] text-white border-transparent'
                  : 'bg-card text-muted-foreground border-border hover:text-foreground'
              )}
            >
              {chip.label}
              <span className={cn('ml-1.5', active ? 'text-white/80' : 'text-muted-foreground/70')}>
                {chip.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16">
          <div className="w-14 h-14 rounded-full bg-[var(--v4-accent)]/10 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7 text-[var(--v4-accent)]" />
          </div>
          <p className="text-base font-medium text-foreground">All clear - no alerts</p>
          <p className="text-sm text-muted-foreground mt-1">
            Your clients are all in good shape right now.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16">
          <div className="w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-base font-medium text-foreground">No {filter} alerts</p>
          <p className="text-sm text-muted-foreground mt-1">Try a different filter.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ sev, items }) => {
            const meta = SEVERITY_META[sev];
            return (
              <div key={sev} className="space-y-3">
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium flex items-center gap-1.5">
                  <span className={meta.text}>{meta.label}</span>
                  <span className="text-muted-foreground/60">({items.length})</span>
                </h3>
                <div className="space-y-3">
                  {items.map((alert) => {
                    const Icon = meta.icon;
                    const card = (
                      <div
                        className={cn(
                          'flex items-start gap-3 p-4 rounded-xl border transition',
                          meta.soft,
                          meta.border,
                          alert.clientId && 'hover:border-[var(--v4-accent)]/30 active:scale-[0.99]'
                        )}
                      >
                        <span
                          className={cn(
                            'shrink-0 h-9 w-9 rounded-full flex items-center justify-center',
                            meta.soft
                          )}
                        >
                          <Icon className={cn('h-5 w-5', meta.text)} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{alert.title}</p>
                          <p className="text-sm text-muted-foreground mt-0.5 break-words">
                            {alert.description}
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {alert.clientName && (
                              <span className={cn('text-xs font-medium', meta.text)}>
                                {alert.clientName}
                              </span>
                            )}
                            {alert.clientName && alert.createdAt && (
                              <span className="text-muted-foreground/50 text-xs">-</span>
                            )}
                            {alert.createdAt && (
                              <span className="text-xs text-muted-foreground">
                                {relativeTime(alert.createdAt)}
                              </span>
                            )}
                          </div>
                        </div>
                        {alert.clientId && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                        )}
                      </div>
                    );

                    return alert.clientId ? (
                      <Link key={alert.id} to={`/pa/client/${alert.clientId}`} className="block">
                        {card}
                      </Link>
                    ) : (
                      <div key={alert.id}>{card}</div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
